import React, { useCallback, useEffect, useState } from "react";
import {
  Badge, Box, Button, Flex, Input, Select, Spinner, Table, TableContainer,
  Tbody, Td, Text, Th, Thead, Tr, useColorMode, useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { cqAPI } from "../../lib/Auth";
import "./Outreach.css";

// The global outreach suppression list. Rows are written by STOP, unsubscribe
// links, call opt-outs, bounces and complaints — and never cleared by anything
// except the delete button on this page. Removing one is re-consenting on the
// contact's behalf; the confirm text says so.

const SOURCE_COLORS = { stop: "red", unsubscribe: "red", call_optout: "red", bounce: "yellow", complaint: "red", manual: "gray" };

function OutreachOptOuts() {
  const toast = useToast();
  const { colorMode } = useColorMode();
  const mutedColor = colorMode === "light" ? "gray.600" : "gray.400";

  const [optouts, setOptouts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [value, setValue] = useState("");
  const [channel, setChannel] = useState("all");
  const [busy, setBusy] = useState(false);

  const fetchOptouts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await cqAPI.get("/outreach/optouts?bid=");
      if (response?.type === "ok") setOptouts(response.data || []);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchOptouts(); }, [fetchOptouts]);

  const add = async () => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      const response = await cqAPI.post("/outreach/optouts", { channel, value: value.trim() });
      if (response?.type === "ok") {
        setValue("");
        await fetchOptouts();
      } else {
        toast({ title: "Not added", description: response?.msg, status: "error", duration: 4000 });
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${row.value} from the suppression list? They will be contactable again — do this only if they asked to be.`)) return;
    const response = await cqAPI.del(`/outreach/optouts/${row._id}`);
    if (response?.type === "ok") await fetchOptouts();
  };

  return (
    <Box className="outreach-page page-pad" p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Text fontSize="xl" fontWeight="bold" textAlign="left" mb={2}>Outreach opt-outs</Text>
      <Text fontSize="sm" color={mutedColor} textAlign="left" mb={4}>
        Everyone here is excluded from every outreach send, on the listed channel. Entries are permanent
        until deliberately removed.
      </Text>

      <Flex gap={2} mb={4} wrap="wrap">
        <Input width={{ base: "100%", md: "280px" }} placeholder="email or phone"
          value={value} onChange={(e) => setValue(e.target.value)} />
        <Select width="120px" value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="all">all</option>
          <option value="email">email</option>
          <option value="sms">sms</option>
          <option value="call">call</option>
        </Select>
        <Button flexShrink={0} onClick={add} isLoading={busy}>Suppress</Button>
      </Flex>

      {isLoading ? (
        <Flex justify="center" py={10}><Spinner size="xl" color="blue.500" /></Flex>
      ) : (
        <TableContainer borderWidth="1px" borderRadius="lg">
          <Table variant="simple" size="sm">
            <Thead><Tr><Th>Value</Th><Th>Channel</Th><Th>Source</Th><Th>When</Th><Th></Th></Tr></Thead>
            <Tbody>
              {optouts.length === 0 ? (
                <Tr><Td colSpan={5}><Text fontSize="sm" color={mutedColor}>Nobody has opted out yet.</Text></Td></Tr>
              ) : optouts.map((row) => (
                <Tr key={row._id}>
                  <Td>{row.value}</Td>
                  <Td><Badge>{row.channel}</Badge></Td>
                  <Td><Badge colorScheme={SOURCE_COLORS[row.source] || "gray"} variant="subtle">{row.source}</Badge></Td>
                  <Td>{moment(row.created).format("MMM D, YYYY")}</Td>
                  <Td>
                    <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<FontAwesomeIcon icon={faTrash} />}
                      onClick={() => remove(row)}>Remove</Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default OutreachOptOuts;
